import React from 'react';
import { formatTranslation } from 'intl';
import { UserTranslationKeys } from 'intl/keys';
import { Divider, SxProps, Theme } from '@mui/material';
import { useBacktrack } from 'components/common/withBacktrack';
import { composeHOCs } from 'shared/utils/composeHOCs';
import { withProcessesData } from 'components/common/withProcessesData';
import { Spinner } from 'components/core';
import {
  useEditUser,
  withEditUser,
} from 'components/dataCreationForms/user/withEditUser';
import { WithInputForm } from 'components/dataCreationForms/withInputForm';
import { useFormCleanupOnUnmount } from 'presentation/dialogForms/dialogFormsHooks';
import { useCallback, memo } from 'react';
import { useIntl } from 'react-intl';
import { withCreateSite } from 'components/dataCreationForms/site/withCreateSite';
import { MemoUserForm } from 'components/user/form';
import { withUsers } from 'components/dataProviders/withUsers';
import ViewWrapper from 'components/core/Layout/ViewWrapper';
import { inspectionSubheaderBaseSx } from 'components/inspection/layout';
import { SubheaderText } from 'components/general';
import { SubmitFooter } from 'components/submitFooter';

const subheaderSx: SxProps<Theme> = {
  ...inspectionSubheaderBaseSx,
  mt: 4,
};

const dividerSx: SxProps<Theme> = {
  my: 4,
};

function EditUserWizardContainer(): React.ReactElement {
  const {
    submitForm,
    isPosting,
    existingValues,
    releaseSubmitEvent,
    SUBMIT_EVENT_TYPE,
    handleFormValidation,
    validate,
  } = useEditUser();
  const intl = useIntl();

  const { back } = useBacktrack();
  const formName = 'user';
  useFormCleanupOnUnmount(formName);
  const onCancel = useCallback(() => {
    back();
  }, [back]);
  // TODO we should not wait for sites.
  // Sometimes location fields renders with existing input, sometimes not.
  // I guess it has something to do with Stateful component logic which does not rerender location field
  // This happens in scenario when it initially received empty array and after this array with some content (once per few form renders)
  // Didn't have time to fix this, needs to be done carefully & without breaking other forms
  if (!existingValues || !existingValues?.sites) {
    return (
      <Spinner
        reason={`EditUserWizardContainer Waiting for existing values: ${existingValues} or sites: ${existingValues?.sites}`}
      />
    );
  }
  return (
    <>
      <ViewWrapper>
        <WithInputForm
          values={existingValues}
          errors={{}}
          handleFormValidation={handleFormValidation}
          handleFieldValidation={validate}
          onSubmit={submitForm}
          eventType={SUBMIT_EVENT_TYPE}
          dispatchSubmit={releaseSubmitEvent}
          formName={formName}
        >
          <SubheaderText sx={subheaderSx}>
            {formatTranslation(intl, UserTranslationKeys.editUserSection)}
          </SubheaderText>

          <Divider sx={dividerSx} />

          <MemoUserForm
            isDialog={false}
            editedUserId={existingValues._id}
            existingRoleId={existingValues.role?._id}
          />
        </WithInputForm>
      </ViewWrapper>

      <SubmitFooter
        isPosting={isPosting}
        onCancel={onCancel}
        onSubmit={releaseSubmitEvent}
      />
    </>
  );
}

export const MemoEditUserWizardContainer = memo(
  composeHOCs(
    withProcessesData,
    withUsers,
    withEditUser,
    withCreateSite
  )(EditUserWizardContainer)
);
